/**
 * usePermissions — centralised permission helper for the entire frontend.
 *
 * Design principles:
 *  - Single source of truth: all permission checks go through this hook.
 *  - Admin short-circuit: role === 'admin' bypasses all granular checks.
 *  - Supervisor obeys granular permissions (mirrors the backend enforcement).
 *  - Null-safe: returns false when user is not authenticated.
 *
 * Usage:
 *   const { can, canAny, canAll, isAdmin, isSupervisor, isSuperAdminUser } = usePermissions();
 *
 *   can('canManageSettings')          // true | false
 *   canAny('canViewReports', 'canViewDashboard')
 *   canAll('canSendMessages', 'canManageTickets')
 */

import { useAuth } from '@/contexts/AuthContext';
import type { UserPermissions } from '@/types';

// Re-export the type for convenience when used with PermissionGuard
export type PermissionKey = keyof UserPermissions;

interface UsePermissionsReturn {
  /** Returns true if the user has the given permission (admin always has all). */
  can: (perm: PermissionKey) => boolean;
  /** Returns true if the user has at least one of the given permissions. */
  canAny: (...perms: PermissionKey[]) => boolean;
  /** Returns true only if the user has ALL of the given permissions. */
  canAll: (...perms: PermissionKey[]) => boolean;
  /** True if role === 'admin'. */
  isAdmin: boolean;
  /** True if role === 'supervisor'. */
  isSupervisor: boolean;
  /** True if role === 'agent'. */
  isAgent: boolean;
  /** True if role === 'admin' AND companyId === 1 (platform superadmin). */
  isSuperAdminUser: boolean;
}

export function usePermissions(): UsePermissionsReturn {
  const { user } = useAuth();

  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  const isAgent = user?.role === 'agent';
  const isSuperAdminUser = isAdmin && user?.companyId === 1;

  /**
   * can — check a single permission key.
   * Admin always returns true regardless of the permissions object.
   */
  const can = (perm: PermissionKey): boolean => {
    if (!user) return false;
    if (isAdmin) return true;
    return user.permissions?.[perm] === true;
  };

  /**
   * canAny — returns true if the user satisfies AT LEAST ONE of the given permissions.
   */
  const canAny = (...perms: PermissionKey[]): boolean => {
    if (!user) return false;
    if (isAdmin) return true;
    return perms.some((p) => user.permissions?.[p] === true);
  };

  /**
   * canAll — returns true only when every permission in the list is satisfied.
   */
  const canAll = (...perms: PermissionKey[]): boolean => {
    if (!user) return false;
    if (isAdmin) return true;
    return perms.every((p) => user.permissions?.[p] === true);
  };

  return { can, canAny, canAll, isAdmin, isSupervisor, isAgent, isSuperAdminUser };
}

export default usePermissions;
